import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Clock, FileText, User, Building } from "lucide-react";
import { AssessmentErrorScreen } from "./AssessmentErrorScreen";
import type { Candidate, College, JobPosition } from "@/types/database";

interface AssessmentStartScreenProps {
  candidate: Candidate;
  college: College;
  jobPosition: JobPosition | null;
  onStartAssessment: () => void;
  isStarting: boolean;
  error?: {
    errorCode?: string;
    error: string;
    availableTime?: string;
    availableDate?: string;
  } | null;
  onRetryStart?: () => void;
}

export function AssessmentStartScreen({
  candidate,
  college,
  jobPosition,
  onStartAssessment,
  isStarting,
  error,
  onRetryStart,
}: AssessmentStartScreenProps) {
  // Show error screen if there's an error
  if (error) {
    return (
      <AssessmentErrorScreen
        candidate={candidate}
        college={college}
        jobPosition={jobPosition}
        error={error}
        onRetry={onRetryStart}
        isRetrying={isStarting}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-gray-900">
            Welcome to Your Assessment
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 mt-2">
            Please review the information below before starting your assessment
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Candidate Information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <User className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">
                Candidate Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <span className="ml-2 text-gray-900">
                  {candidate.full_name}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-900">{candidate.email}</span>
              </div>
              {candidate.phone && (
                <div>
                  <span className="font-medium text-gray-700">Phone:</span>
                  <span className="ml-2 text-gray-900">{candidate.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* College Information */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <Building className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-green-900">
                Assessment Details
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">College:</span>
                <span className="ml-2 text-gray-900">{college.name}</span>
              </div>
              {jobPosition && (
                <div>
                  <span className="font-medium text-gray-700">Position:</span>
                  <span className="ml-2 text-gray-900">
                    {jobPosition.role_name}
                  </span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Duration:</span>
                <span className="ml-2 text-gray-900">
                  {college.duration_minutes} minutes
                </span>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-5 w-5 text-amber-600" />
              <h3 className="text-lg font-semibold text-amber-900">
                Important Instructions
              </h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>
                  Once you start the assessment, the timer will begin
                  automatically
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>
                  You have <strong>{college.duration_minutes} minutes</strong>{" "}
                  to complete all questions
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>The assessment will auto-submit when time expires</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>Make sure you have a stable internet connection</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>
                  Do not refresh the page or navigate away during the assessment
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>
                  Your browser will go fullscreen automatically. Keep it
                  fullscreen until you finish
                </span>
              </li>
            </ul>
          </div>

          {/* Start Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={onStartAssessment}
              disabled={isStarting}
              size="lg"
              className="px-8 py-3 text-lg font-semibold"
            >
              {isStarting ? (
                <>
                  <Clock className="h-5 w-5 mr-2 animate-spin" />
                  Starting Assessment...
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 mr-2" />
                  Start Assessment
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
